// A text people copy into Claude or a terminal: shown in full (wrapped, in the mono face; a prompt in plain words reads
// in the UI sans) with one Copy button. The one prompt block: empty steps and "Work on this with Claude" both use it.
// Without a clipboard (insecure origin, blocked) the text is selected instead, so the system copy works.
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api.ts";
import { activeConnections, connectApi } from "../../data/connect.ts";
import { Icon } from "../icons.tsx";
import "./connect.css";

export function CopyText({
  text,
  shown,
  label,
  copyLabel = "Copy",
  secret = false,
  prose = false,
}: {
  text: string;
  /** what is drawn, when it differs from what is copied (a prompt in plain words; the copy carries the ids) */
  shown?: string;
  /** what is being copied, for screen readers ("Connector address") */
  label: string;
  copyLabel?: string;
  /** a key: the block gets a stronger outline */
  secret?: boolean;
  /** a prompt for Claude: read as words (the UI sans), not as a code */
  prose?: boolean;
}) {
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  // the clipboard refused: the full text (with its ids) is drawn and selected, so the system copy takes all of it
  const [full, setFull] = useState(false);
  const box = useRef<HTMLElement>(null);
  useEffect(() => {
    if (copied !== "yes") return;
    const t = setTimeout(() => setCopied(null), 2400);
    return () => clearTimeout(t);
  }, [copied]);
  const select = () => {
    const el = box.current;
    const sel = window.getSelection();
    if (!el || !sel) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    sel.removeAllRanges();
    sel.addRange(range);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied("yes");
    } catch {
      setFull(true);
      requestAnimationFrame(select);
      setCopied("no");
    }
  };
  return (
    <div className={`cc-copy${secret ? " secret" : ""}${prose ? " prose" : ""}`}>
      <code ref={box} className="cc-copy-text">
        {prose ? (full || !shown ? text : shown) : tokens(full || !shown ? text : shown)}
      </code>
      <button
        type="button"
        className="k-btn k-outline k-sm cc-copy-btn"
        aria-label={`${copyLabel}: ${label}`}
        onClick={() => void copy()}
      >
        <Icon name={copied === "yes" ? "check" : "copy"} size={12} />
        {copied === "yes" ? "Copied" : copyLabel}
      </button>
      {copied === "no" && (
        <p className="cc-copy-note" role="status">
          Copying did not work here. The text is selected: copy it with {navigatorMod()}+C.
        </p>
      )}
      <span className="sr-only" role="status">
        {copied === "yes" ? `${label} copied` : ""}
      </span>
    </div>
  );
}

/** The text as words that do not break inside (spaces kept, so a selection copies the same text). */
function tokens(text: string) {
  return text.split(/(\s+)/).map((t, i) =>
    /^\s*$/.test(t) ? (
      t
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: the words of one fixed text, in order
      <span key={i} className={`cc-tok${t.length > 28 ? " long" : ""}`}>
        {t}
      </span>
    ),
  );
}

const navigatorMod = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? "⌘"
    : "Ctrl";

// ── is a Claude connected? ─────────────────────────────────────────────────────

const known = new Map<string, Promise<boolean | null>>();

/** Forget what is known for a workspace (after connecting or disconnecting in Settings). */
export const forgetConnected = (ws: string) => known.delete(ws);

/**
 * Has this person a live Claude connection in the workspace? undefined while it is being asked, null when the
 * list cannot be read (then the screens just show the way to Settings → Claude). Asked once per workspace, and
 * only when `enabled`.
 */
export function useClaudeConnected(api: Api, ws: string, enabled = true): boolean | null | undefined {
  const [v, setV] = useState<boolean | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let p = known.get(ws);
    if (!p) {
      p = connectApi(api)
        .connections(ws)
        .then(
          (items) => activeConnections(items).length > 0,
          () => null,
        );
      known.set(ws, p);
      void p.then((r) => r === null && known.delete(ws));
    }
    void p.then((r) => live && setV(r));
    return () => {
      live = false;
    };
  }, [api, ws, enabled]);
  return v;
}
